import { Injectable } from '@angular/core';
import { wallDisplayedSize } from './wall-api.service';

/** Central tunables for overlapping-row gallery layout. */
export const GALLERY_ROWS_TUNABLES = {
  weightMin: 0.65,
  weightMax: 2.2,
  defaultAspect: 0.75,
  heightVariationMin: 0.95,
  heightVariationMax: 1.05,
  /** Max (maxH-minH)/baseHeight within a row. */
  maxHeightSpread: 0.1,
  /** Max overlap depth vs row base height (desktop/tablet). */
  maxOverlapRatio: 0.2,
  /** Max overlap on mobile. */
  mobileOverlapRatio: 0.08,
  gapDesktop: 10,
  gapTablet: 8,
  gapMobile: 6,
  /** Weighted capacity ≈ “normal” works per row. */
  capacity: {
    wide: 5,
    desktop: 4,
    tablet: 3,
    mobile: 2,
  },
  /**
   * Kept for callers that still pass `{ personal: true }`; personal wall now
   * uses full exhibition width/capacity, so prefer the main `capacity` table.
   */
  personalCapacity: {
    wide: 3.6,
    desktop: 3.6,
    tablet: 2.4,
    mobile: 2.4,
  },
  baseHeight: {
    wide: 340,
    desktop: 300,
    tablet: 260,
    mobile: 210,
  },
  minHeight: {
    wide: 240,
    desktop: 220,
    tablet: 190,
    mobile: 160,
  },
  maxHeight: {
    wide: 420,
    desktop: 380,
    tablet: 320,
    mobile: 260,
  },
  /** Recompute layout when container width changes by at least this many px. */
  resizeThresholdPx: 12,
  resizeDebounceMs: 120,
} as const;

export type GalleryRowsBreakpoint = 'wide' | 'desktop' | 'tablet' | 'mobile';

export interface GalleryRowsWorkInput {
  _id: string;
  natW?: number;
  natH?: number;
  rotation?: number;
}

/** Dummy collage card used while feed / thumbs are in-flight. */
export interface WallSkeletonWork {
  _id: string;
  natW: number;
  natH: number;
  isSkeleton: true;
  /** Present so gallery templates can bind without narrowing. */
  commentsMounted?: false;
  commentsOpen?: false;
  id?: string;
}

export function isWallSkeletonWork(work: unknown): work is WallSkeletonWork {
  return (
    !!work &&
    typeof work === 'object' &&
    (work as WallSkeletonWork).isSkeleton === true
  );
}

/** Typical photo aspects so first-load dummies look like a real collage. */
const WALL_SKELETON_SIZES: Array<{ natW: number; natH: number }> = [
  { natW: 300, natH: 400 },
  { natW: 400, natH: 300 },
  { natW: 360, natH: 360 },
  { natW: 320, natH: 480 },
  { natW: 480, natH: 270 },
  { natW: 500, natH: 400 },
];

/** How many dummy cards to show (≈ `rows` gallery rows at this width). */
export function wallSkeletonCount(width: number, rows = 2): number {
  const w = Number(width) || 960;
  let bp: GalleryRowsBreakpoint = 'desktop';
  if (w <= 640) {
    bp = 'mobile';
  } else if (w <= 820) {
    bp = 'tablet';
  } else if (w > 1100) {
    bp = 'wide';
  }
  return Math.max(2, Math.ceil(GALLERY_ROWS_TUNABLES.capacity[bp]) * rows);
}

/**
 * Dummy works for collage layout. When `source` is given, copy displayed
 * sizes so load-more placeholders match cards already on screen.
 */
export function makeWallSkeletonWorks(
  count: number,
  idPrefix: string,
  source?: Array<{ natW?: number; natH?: number; rotation?: number }>
): WallSkeletonWork[] {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  return Array.from({ length: n }, (_, i) => {
    const src = source?.length ? source[i % source.length] : null;
    const displayed = src
      ? wallDisplayedSize(src.natW, src.natH, src.rotation)
      : { w: 0, h: 0 };
    const fallback = WALL_SKELETON_SIZES[i % WALL_SKELETON_SIZES.length];
    return {
      _id: `${idPrefix}-${i}`,
      natW: displayed.w > 0 ? displayed.w : fallback.natW,
      natH: displayed.h > 0 ? displayed.h : fallback.natH,
      isSkeleton: true as const,
    };
  });
}

export interface GalleryLayoutItem<T extends GalleryRowsWorkInput = GalleryRowsWorkInput> {
  work: T;
  aspectRatio: number;
  weight: number;
  width: number;
  height: number;
  /** Deterministic height factor in [variationMin, variationMax]. */
  heightFactor: number;
}

export interface GalleryLayoutRow<T extends GalleryRowsWorkInput = GalleryRowsWorkInput> {
  items: GalleryLayoutItem<T>[];
  top: number;
  baseHeight: number;
  minHeight: number;
  maxHeight: number;
  overlap: number;
  zIndex: number;
  isLast: boolean;
}

export interface GalleryRowsLayoutResult<T extends GalleryRowsWorkInput = GalleryRowsWorkInput> {
  rows: GalleryLayoutRow<T>[];
  totalHeight: number;
  gap: number;
  breakpoint: GalleryRowsBreakpoint;
  containerWidth: number;
}

@Injectable({ providedIn: 'root' })
export class GalleryRowsLayoutService {
  readonly tunables = GALLERY_ROWS_TUNABLES;

  breakpointForWidth(width: number): GalleryRowsBreakpoint {
    const w = Number(width) || 0;
    if (w <= 640) {
      return 'mobile';
    }
    if (w <= 820) {
      return 'tablet';
    }
    if (w <= 1100) {
      return 'desktop';
    }
    return 'wide';
  }

  gapForBreakpoint(bp: GalleryRowsBreakpoint): number {
    const t = this.tunables;
    if (bp === 'mobile') {
      return t.gapMobile;
    }
    if (bp === 'tablet') {
      return t.gapTablet;
    }
    return t.gapDesktop;
  }

  capacityForBreakpoint(bp: GalleryRowsBreakpoint): number {
    return this.tunables.capacity[bp];
  }

  /**
   * Build overlapping horizontal rows in chronological order (newest first).
   * No reordering across rows; packing decisions are deterministic.
   */
  layout<T extends GalleryRowsWorkInput>(
    works: T[],
    containerWidth: number,
    options?: {
      gap?: number;
      breakpoint?: GalleryRowsBreakpoint;
      /** Override weighted capacity for this layout pass. */
      capacity?: number;
      /** Use personal-wall capacity table (narrower main column). */
      personal?: boolean;
    }
  ): GalleryRowsLayoutResult<T> {
    const width = Math.max(0, Number(containerWidth) || 0);
    const breakpoint = options?.breakpoint || this.breakpointForWidth(width);
    const gap =
      options?.gap != null ? options.gap : this.gapForBreakpoint(breakpoint);
    const capacityTable = options?.personal
      ? this.tunables.personalCapacity
      : this.tunables.capacity;
    const capacity =
      options?.capacity != null
        ? options.capacity
        : capacityTable[breakpoint];

    if (!works.length || width <= 0) {
      return {
        rows: [],
        totalHeight: 0,
        gap,
        breakpoint,
        containerWidth: width,
      };
    }

    const prepared = works.map((work) => this.prepareItem(work));
    const groups = this.splitIntoRows(prepared, capacity);
    const rows: GalleryLayoutRow<T>[] = [];
    let top = 0;
    const totalRows = groups.length;
    const zBase = totalRows + 10;

    groups.forEach((group, rowIndex) => {
      const isLast = rowIndex === totalRows - 1;
      const sized = this.sizeRowItems(
        group,
        width,
        gap,
        breakpoint,
        isLast,
        capacity
      );
      const baseHeight = this.tunables.baseHeight[breakpoint];
      const rowHeight = sized[0]?.height ?? baseHeight;

      rows.push({
        items: sized,
        top,
        baseHeight,
        minHeight: rowHeight,
        maxHeight: rowHeight,
        overlap: 0,
        zIndex: zBase - rowIndex,
        isLast,
      });

      top += Math.max(1, rowHeight + gap);
    });

    const last = rows[rows.length - 1];
    const totalHeight = last ? last.top + last.maxHeight : 0;

    return {
      rows,
      totalHeight,
      gap,
      breakpoint,
      containerWidth: width,
    };
  }

  stableHash(id: string): number {
    const s = String(id || '');
    let h = 2166136261;
    for (let i = 0; i < s.length; i++) {
      h ^= s.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return h >>> 0;
  }

  private prepareItem<T extends GalleryRowsWorkInput>(
    work: T
  ): Omit<GalleryLayoutItem<T>, 'width' | 'height'> {
    const aspectRatio = this.aspectRatio(work);
    const weight = this.clamp(
      aspectRatio,
      this.tunables.weightMin,
      this.tunables.weightMax
    );
    const heightFactor = this.heightFactorFor(work._id);
    return { work, aspectRatio, weight, heightFactor };
  }

  private aspectRatio(work: GalleryRowsWorkInput): number {
    const { w, h } = wallDisplayedSize(work.natW, work.natH, work.rotation);
    if (w > 0 && h > 0) {
      return w / h;
    }
    return this.tunables.defaultAspect;
  }

  /**
   * Continuous deterministic factor in [0.9, 1.1] from id hash —
   * avoids index-based high/low/high stripes.
   */
  private heightFactorFor(id: string): number {
    const t = this.tunables;
    const u = (this.stableHash(id) % 10007) / 10007;
    return t.heightVariationMin + (t.heightVariationMax - t.heightVariationMin) * u;
  }

  private splitIntoRows<T extends GalleryRowsWorkInput>(
    items: Array<Omit<GalleryLayoutItem<T>, 'width' | 'height'>>,
    capacity: number
  ): Array<Array<Omit<GalleryLayoutItem<T>, 'width' | 'height'>>> {
    const rows: Array<Array<Omit<GalleryLayoutItem<T>, 'width' | 'height'>>> = [];
    let current: Array<Omit<GalleryLayoutItem<T>, 'width' | 'height'>> = [];
    let sum = 0;

    for (const item of items) {
      const w = item.weight;
      if (!current.length) {
        current.push(item);
        sum = w;
        continue;
      }

      if (sum + w <= capacity) {
        current.push(item);
        sum += w;
        continue;
      }

      // Overflow: keep in this row if closer to capacity than leaving it out.
      const withItem = sum + w;
      const distKeep = Math.abs(withItem - capacity);
      const distLeave = Math.abs(sum - capacity);
      if (distKeep <= distLeave) {
        current.push(item);
        rows.push(current);
        current = [];
        sum = 0;
      } else {
        rows.push(current);
        current = [item];
        sum = w;
      }
    }

    if (current.length) {
      rows.push(current);
    }
    return rows;
  }

  private sizeRowItems<T extends GalleryRowsWorkInput>(
    group: Array<Omit<GalleryLayoutItem<T>, 'width' | 'height'>>,
    containerWidth: number,
    gap: number,
    breakpoint: GalleryRowsBreakpoint,
    isLast: boolean,
    capacity: number
  ): GalleryLayoutItem<T>[] {
    const n = group.length;
    const gapsTotal = Math.max(0, n - 1) * gap;
    const available = Math.max(0, containerWidth - gapsTotal);
    const weightSum = group.reduce((s, it) => s + it.weight, 0) || 1;
    const t = this.tunables;
    const baseH = t.baseHeight[breakpoint];
    const minH = t.minHeight[breakpoint];
    const maxH = t.maxHeight[breakpoint];

    // Full rows stretch to fill; last row stays start-aligned (unit-sized).
    const unit = available / capacity;
    const widths = group.map((it) => {
      if (isLast && weightSum < capacity * 0.92) {
        return Math.max(48, unit * it.weight);
      }
      return Math.max(48, (available * it.weight) / weightSum);
    });

    // If last-row natural widths overflow, scale down proportionally.
    if (isLast) {
      const sumW = widths.reduce((a, b) => a + b, 0);
      if (sumW > available && sumW > 0) {
        const scale = available / sumW;
        for (let i = 0; i < widths.length; i++) {
          widths[i] *= scale;
        }
      }
    }

    const naturalHeights = group.map((it, i) => {
      const natural = it.aspectRatio > 0 ? widths[i] / it.aspectRatio : baseH;
      return natural;
    });
    let rowHeight = Math.max(...naturalHeights, minH);
    rowHeight = this.clamp(rowHeight, minH, maxH);

    let exactWidths = group.map((it) =>
      it.aspectRatio > 0 ? rowHeight * it.aspectRatio : rowHeight * 0.8
    );

    const sumExactW = exactWidths.reduce((a, b) => a + b, 0);
    const shouldFillRow = !isLast || weightSum >= capacity * 0.92;
    // Always fit when overflowing; full rows also stretch to fill width.
    if (sumExactW > 0 && (sumExactW > available + 0.5 || shouldFillRow)) {
      const scale = available / sumExactW;
      rowHeight *= scale;
      exactWidths = exactWidths.map((w) => w * scale);
    }

    const rowHeightFinal = rowHeight;

    return group.map((it, i) => ({
      ...it,
      width: exactWidths[i],
      height: rowHeightFinal,
    }));
  }

  private clamp(n: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, n));
  }
}
